import React, { FC, useState, useEffect } from 'react'
import isEqual from 'lodash/isEqual'
import { FilterAutocomplete } from 'transfix-ui/components/FilterAutocomplete'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { RadioOption } from 'transfix-ui/components/RadioOption'
import { Select, ISelectProps } from 'transfix-ui/components/Select'
import { Divider, Skeleton } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme'
import { InputMatrix } from 'apps/marketplace/components/InputMatrix'
import { useRequestForProposalStrategiesQuery } from 'apps/marketplace/components/rfp/graphql/RequestForProposalStrategies'
import {
  EditCategories,
  EligibilityCriteriaFields,
  ThresholdManagementFields,
  ValidStrategyMatrices,
  ILaneCriteria,
  DropTypes,
  Strategy,
} from 'apps/marketplace/components/rfp/types/strategyManager'
import {
  formatAllStrategyData,
  serializeAllStrategyData,
  getStrategyNameByKey,
  getEquipmentDisplayValue,
  getDropStrategy,
  getDropTypeDisplayValue,
  getRiskyAuditDisplayValue,
  getContractDurationDisplayValue,
  getPuMarketCenterDistanceDisplayValue,
  getDelMarketCenterDistanceDisplayValue,
  getAnnualVolumeDisplayValue,
  getMarketDemandScoreDisplayValue,
  getExtraStopsDisplayValue,
  getEffectiveMileageDisplayValue,
  getDatVariance,
  getDatVarianceDisplayValue,
  getNegativeMarginRisk,
  getNegativeMarginRiskDisplayValue,
  canDisplayMinimumMargin,
  getMinimumMarginDisplayValue,
  thresholdManagementFieldsPresent,
  generateDropStrategyOptions,
  findLaneCriteriaByDropType,
  buildVolumeByLengthOfHaulMatrix,
  buildDesirabilityByPickupMarketMatrix,
  buildEquipmentByPickupMarketMatrix,
  buildVolumeToDesirabilityLowMatrix,
  buildVolumeToDesirabilityMediumMatrix,
  buildVolumeToDesirabilityHighMatrix,
  buildLengthOfHaulToCostPctDat7Matrix,
} from 'apps/marketplace/components/rfp/utils/strategyManager'
import { Spinner } from 'transfix-ui'
import { EditableField } from './EditableField'
import { EditableFieldInput } from './EditableFieldInput'

const useStyles = makeStyles({
  heading: {
    borderBottom: `1px solid ${brandColors.coolGray3}`,
  },
  subHeading: {
    fontSize: '1.2rem',
  },
  sidePanel: {
    backgroundColor: brandColors.infoLight,
  },
})

interface IDropdownOption {
  id: string
  label: string
}

interface IStrategyManagementTabProps {
  setCurrentStrategyId: (strategyId: string) => void
  updatedStrategies: Strategy[]
  refetchStrategies: boolean
  rfpId: string | null | undefined
  setIsSaveDisabled: Function
  setStrategyStateIsValid: Function
  setRefetchStrategies: Function
  setStrategies: Function
  setUpdatedStrategies: Function
  strategyStateIsValid: boolean
}

export const StrategyManagementTab: FC<IStrategyManagementTabProps> = ({
  refetchStrategies,
  rfpId,
  setCurrentStrategyId,
  setIsSaveDisabled,
  setRefetchStrategies,
  setStrategies,
  setStrategyStateIsValid,
  setUpdatedStrategies,
  strategyStateIsValid,
  updatedStrategies,
}) => {
  const classes = useStyles()
  const [currentStrategy, setCurrentStrategy] = useState<Strategy | null | undefined>(null)
  const [currentMatrix, setCurrentMatrix] = useState<ValidStrategyMatrices | null>(null)
  const [currentLaneCriteria, setCurrentLaneCriteria] = useState<ILaneCriteria | null>(null)
  const [currentEditCategory, setCurrentEditCategory] = useState<EditCategories | null>(null)
  const [currentEditField, setCurrentEditField] = useState<
    EligibilityCriteriaFields | ThresholdManagementFields | null
  >(null)

  const { data, loading, refetch } = useRequestForProposalStrategiesQuery({
    variables: {
      rfpId,
    },
  })
  const { requestForProposalStrategies } = data || {}

  const strategyOptions = requestForProposalStrategies
    ? requestForProposalStrategies.map(strategy => ({
        label: strategy.name,
        id: strategy.id,
      }))
    : []

  const currentOptionStrategy = strategyOptions.find(({ id }) => id === currentStrategy?.id)

  const getDropdownLabel = (opt: IDropdownOption) => opt.label
  const compareDropdownIds = (optA: IDropdownOption, optB: IDropdownOption) => optA?.id === optB?.id

  const onStrategySelect = (
    opt: string | IDropdownOption | (string | IDropdownOption)[] | null
  ) => {
    let selectedStrategy = null
    const option = opt as IDropdownOption | null

    if (option) {
      selectedStrategy = updatedStrategies.find(strategy => strategy.id === option.id)

      if (!selectedStrategy && requestForProposalStrategies) {
        selectedStrategy = requestForProposalStrategies.find(strategy => strategy.id === option.id)
      }

      if (selectedStrategy) {
        setCurrentStrategy(formatAllStrategyData(selectedStrategy))
      }
    }
  }

  const onSelectedMatrixChange = (matrix: ValidStrategyMatrices) => {
    setCurrentMatrix(matrix)
  }

  const onVolToLohChange = (lengthOfHaul: string, volumeLevel: string, value: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        volumeToLengthOfHaul: {
          ...currentStrategy?.strategyMatrix.volumeToLengthOfHaul,
          matrix: {
            ...currentStrategy?.strategyMatrix.volumeToLengthOfHaul.matrix,
            [lengthOfHaul]: {
              ...currentStrategy?.strategyMatrix.volumeToLengthOfHaul.matrix[lengthOfHaul],
              [volumeLevel]: value,
            },
          },
        },
      },
    })
  }

  const onDesirabilityByPuMarketChange = (
    puMarketType: string,
    desirabilityLevel: string,
    value: string
  ) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        desirabilityToPickupMarket: {
          ...currentStrategy?.strategyMatrix.desirabilityToPickupMarket,
          matrix: {
            ...currentStrategy?.strategyMatrix.desirabilityToPickupMarket.matrix,
            [desirabilityLevel]: {
              ...currentStrategy?.strategyMatrix.desirabilityToPickupMarket.matrix[
                desirabilityLevel
              ],
              [puMarketType]: value,
            },
          },
        },
      },
    })
  }

  const onEquipToPuMarketChange = (puMarketType: string, equipmentType: string, value: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        equipmentToPickupMarket: {
          ...currentStrategy?.strategyMatrix.equipmentToPickupMarket,
          matrix: {
            ...currentStrategy?.strategyMatrix.equipmentToPickupMarket.matrix,
            [equipmentType]: {
              ...currentStrategy?.strategyMatrix.equipmentToPickupMarket.matrix[equipmentType],
              [puMarketType]: value,
            },
          },
        },
      },
    })
  }

  const onVolToDesirLowChange = (desirability: string, volume: string, newValue: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        volumeToDesirabilityLow: {
          ...currentStrategy?.strategyMatrix.volumeToDesirabilityLow,
          matrix: {
            ...currentStrategy?.strategyMatrix.volumeToDesirabilityLow.matrix,
            [desirability]: {
              ...currentStrategy?.strategyMatrix.volumeToDesirabilityLow.matrix[desirability],
              [volume]: newValue,
            },
          },
        },
      },
    })
  }

  const onVolToDesirMedChange = (desirability: string, volume: string, newValue: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        volumeToDesirabilityMedium: {
          ...currentStrategy?.strategyMatrix.volumeToDesirabilityMedium,
          matrix: {
            ...currentStrategy?.strategyMatrix.volumeToDesirabilityMedium.matrix,
            [desirability]: {
              ...currentStrategy?.strategyMatrix.volumeToDesirabilityMedium.matrix[desirability],
              [volume]: newValue,
            },
          },
        },
      },
    })
  }

  const onVolToDesirHighChange = (desirability: string, volume: string, newValue: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        volumeToDesirabilityHigh: {
          ...currentStrategy?.strategyMatrix.volumeToDesirabilityHigh,
          matrix: {
            ...currentStrategy?.strategyMatrix.volumeToDesirabilityHigh.matrix,
            [desirability]: {
              ...currentStrategy?.strategyMatrix.volumeToDesirabilityHigh.matrix[desirability],
              [volume]: newValue,
            },
          },
        },
      },
    })
  }

  const onLohToCostPctDatChange = (lengthOfHaul: string, newValue: string) => {
    setCurrentStrategy({
      ...(currentStrategy || {}),
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        lengthOfHaulToCostPctDat7: {
          ...currentStrategy?.strategyMatrix.lengthOfHaulToCostPctDat7,
          matrix: {
            ...currentStrategy?.strategyMatrix.lengthOfHaulToCostPctDat7.matrix,
            [lengthOfHaul]: newValue,
          },
        },
      },
    })
  }

  const { strategyMatrix } = currentStrategy || {}
  const { laneCriteria } = strategyMatrix || {}
  const presentStrategies = Object.values(ValidStrategyMatrices).filter(
    strategyKey => strategyMatrix && strategyMatrix[strategyKey] !== undefined
  )
  const {
    desirabilityToPickupMarket: desToPuMarket,
    equipmentToPickupMarket: equipToPuMarket,
    lengthOfHaulToCostPctDat7: lohToCostPctDat7,
    volumeToDesirabilityHigh: volToDesirHigh,
    volumeToDesirabilityLow: volToDesirLow,
    volumeToDesirabilityMedium: volToDesirMed,
    volumeToLengthOfHaul: volToLoh,
  } = strategyMatrix || {}

  // DA: Create map for easy lookup of matrix validation results:
  const validityMap: Record<string, boolean> = {}

  const {
    columnLabels: volumeByLengthOfHaulColumns,
    isValid: volumeByLengthOfHaulMatrixIsValid,
    matrix: volumeByLengthOfHaulMatrix,
    rowLabels: volumeByLengthOfHaulRows,
  } = buildVolumeByLengthOfHaulMatrix(volToLoh?.matrix, onVolToLohChange)
  validityMap[ValidStrategyMatrices.volumeToLengthOfHaul] = volumeByLengthOfHaulMatrixIsValid

  const {
    columnLabels: desToPuMarketColumns,
    isValid: desirabilityToPickupMarketMatrixIsValid,
    matrix: desirabilityToPickupMarketMatrix,
    rowLabels: desToPuMarketRows,
  } = buildDesirabilityByPickupMarketMatrix(desToPuMarket?.matrix, onDesirabilityByPuMarketChange)
  validityMap[
    ValidStrategyMatrices.desirabilityToPickupMarket
  ] = desirabilityToPickupMarketMatrixIsValid

  const {
    columnLabels: equipmentToPickupMarketColumns,
    isValid: equipmentToPickupMarketMatrixIsValid,
    matrix: equipmentToPickupMarketMatrix,
    rowLabels: equipmentToPickupMarketRows,
  } = buildEquipmentByPickupMarketMatrix(equipToPuMarket?.matrix, onEquipToPuMarketChange)
  validityMap[ValidStrategyMatrices.equipmentToPickupMarket] = equipmentToPickupMarketMatrixIsValid

  const {
    columnLabels: volumeToDesirabilityLowColumns,
    isValid: volumeToDesirabilityLowMatrixIsValid,
    matrix: volumeToDesirabilityLowMatrix,
    rowLabels: volumeToDesirabilityLowRows,
  } = buildVolumeToDesirabilityLowMatrix(volToDesirLow?.matrix, onVolToDesirLowChange)
  validityMap[ValidStrategyMatrices.volumeToDesirabilityLow] = volumeToDesirabilityLowMatrixIsValid

  const {
    columnLabels: volumeToDesirabilityMediumColumns,
    isValid: volumeToDesirabilityMediumMatrixIsValid,
    matrix: volumeToDesirabilityMediumMatrix,
    rowLabels: volumeToDesirabilityMediumRows,
  } = buildVolumeToDesirabilityMediumMatrix(volToDesirMed?.matrix, onVolToDesirMedChange)
  validityMap[
    ValidStrategyMatrices.volumeToDesirabilityMedium
  ] = volumeToDesirabilityMediumMatrixIsValid

  const {
    columnLabels: volumeToDesirabilityHighColumns,
    isValid: volumeToDesirabilityHighMatrixIsValid,
    matrix: volumeToDesirabilityHighMatrix,
    rowLabels: volumeToDesirabilityHighRows,
  } = buildVolumeToDesirabilityHighMatrix(volToDesirHigh?.matrix, onVolToDesirHighChange)
  validityMap[
    ValidStrategyMatrices.volumeToDesirabilityHigh
  ] = volumeToDesirabilityHighMatrixIsValid

  const {
    columnLabels: lengthOfHaulToCostPctDat7Columns,
    isValid: lengthOfHaulToCostPctDat7MatrixIsValid,
    matrix: lengthOfHaulToCostPctDat7Matrix,
    rowLabels: lengthOfHaulToCostPctDat7Rows,
  } = buildLengthOfHaulToCostPctDat7Matrix(lohToCostPctDat7?.matrix, onLohToCostPctDatChange)
  validityMap[
    ValidStrategyMatrices.lengthOfHaulToCostPctDat7
  ] = lengthOfHaulToCostPctDat7MatrixIsValid

  const allPresentMatricesAreValid = presentStrategies.every(strategy => !!validityMap[strategy])

  const editField = (
    category: EditCategories,
    field: EligibilityCriteriaFields | ThresholdManagementFields
  ) => {
    setCurrentEditCategory(category)
    setCurrentEditField(field)
  }

  const onDropStrategyChange: ISelectProps['onChange'] = e => {
    e.stopPropagation()
    const dropType = e.target.value
    const selectedLaneCriteria = laneCriteria
      ? findLaneCriteriaByDropType(laneCriteria, dropType as DropTypes)
      : undefined

    if (selectedLaneCriteria) {
      setCurrentLaneCriteria(selectedLaneCriteria)
    }
  }

  const onLaneCriteriaChange = (changes: Partial<ILaneCriteria>) => {
    setCurrentLaneCriteria({
      isPuDrop: !!currentLaneCriteria?.isPuDrop,
      isDelDrop: !!currentLaneCriteria?.isDelDrop,
      ...currentLaneCriteria,
      ...changes,
    })
    setCurrentStrategy({
      ...currentStrategy,
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        laneCriteria: currentStrategy?.strategyMatrix?.laneCriteria?.map(
          (criteria: ILaneCriteria) => {
            const criteriaDropStrategy = getDropStrategy(criteria)
            // DA: Type cast to remove null as an option:
            const activeCriteriaDropStrategy = getDropStrategy(currentLaneCriteria as ILaneCriteria)

            if (criteriaDropStrategy === activeCriteriaDropStrategy) {
              return {
                ...criteria,
                ...changes,
              }
            }

            return criteria
          }
        ),
      },
    })
  }

  const onStrategyChange = (changes: Strategy) => {
    setCurrentStrategy({
      ...currentStrategy,
      ...changes,
    })
  }

  // DA: set the current strategy only when the API request
  // has resolved. We use the current strategy state to
  // let users select between all strategies.
  useEffect(() => {
    if (requestForProposalStrategies && requestForProposalStrategies.length > 0) {
      if (currentStrategy) {
        const targetStrategy = requestForProposalStrategies.find(
          strategy => strategy.name === currentStrategy.name
        )
        if (targetStrategy) {
          setCurrentStrategy(formatAllStrategyData(targetStrategy))
        } else {
          setCurrentStrategy(null)
        }
      } else {
        setStrategies(requestForProposalStrategies)
        const targetStrategy = requestForProposalStrategies[0]
        if (targetStrategy) {
          setCurrentStrategy(formatAllStrategyData(targetStrategy))
        } else {
          setCurrentStrategy(null)
        }
      }
    }
  }, [requestForProposalStrategies])

  // DA: set the current matrix only once the matrices for
  // the current strategy have been identified. This only
  // needs to be done once, when the API request has resolved.
  // After that the current matrix is updated using the
  // resulting radio button options for each matrix.
  useEffect(() => {
    if (
      presentStrategies.length > 0 &&
      (currentMatrix === null || !presentStrategies.includes(currentMatrix))
    ) {
      setCurrentMatrix(presentStrategies[0])
    }
  }, [presentStrategies])

  // DA: once there is at least one lane criteria object
  // present, set the initial lane criteria state to the
  // first entry in the array:
  useEffect(() => {
    if (laneCriteria && laneCriteria.length > 0) {
      // We only want to update the current lane criteria
      // if the user hasn't already selected one to view:
      const currentIndex = laneCriteria.findIndex((lane: ILaneCriteria) => {
        return (
          lane.isPuDrop === currentLaneCriteria?.isPuDrop &&
          lane.isDelDrop === currentLaneCriteria?.isDelDrop
        )
      })
      if (currentIndex === -1) {
        setCurrentLaneCriteria(laneCriteria[0])
      }
    } else {
      setCurrentLaneCriteria(null)
    }
  }, [laneCriteria])

  // DA: Synchronize the current validation state so
  // that we can disable the save button for the modal
  // if necessary:
  useEffect(() => {
    if (strategyStateIsValid !== allPresentMatricesAreValid) {
      setStrategyStateIsValid(allPresentMatricesAreValid)
    }
  }, [allPresentMatricesAreValid])

  // CS: persisting the updates made to the current stratey.
  // So that we can use the persisted value when the user hits 'Save'
  useEffect(() => {
    if (currentStrategy) {
      const serializedCurrentStrategy = serializeAllStrategyData(currentStrategy)
      const tempStrategies: Strategy[] = []
      let isSaveDisabled = true

      updatedStrategies.forEach(updatedStrategy => {
        if (updatedStrategy.id !== serializedCurrentStrategy.id) {
          tempStrategies.push(updatedStrategy)
        }

        requestForProposalStrategies?.forEach(strategy => {
          if (strategy.id === serializedCurrentStrategy.id) {
            isSaveDisabled =
              isSaveDisabled &&
              isEqual(strategy.strategyMatrix, serializedCurrentStrategy.strategyMatrix)
          } else if (strategy.id === updatedStrategy.id) {
            isSaveDisabled =
              isSaveDisabled && isEqual(strategy.strategyMatrix, updatedStrategy.strategyMatrix)
          }
        })
      })

      setUpdatedStrategies([...tempStrategies, serializedCurrentStrategy])
      setIsSaveDisabled(isSaveDisabled)

      if (serializedCurrentStrategy?.id) {
        setCurrentStrategyId(serializedCurrentStrategy.id)
      }
    }
  }, [currentStrategy])

  // CS: Refetching strategies when they're saved.
  useEffect(() => {
    if (refetchStrategies) {
      refetch()
      setUpdatedStrategies([])
      setRefetchStrategies(false)
    }
  }, [refetchStrategies])

  const resolveErrorsMessage = 'Please resolve matrix errors before changing strategies.'

  return (
    <>
      <Box px={3} py={2} className={classes.heading}>
        {loading && <Skeleton width={120} height={20} />}
        {!loading && currentStrategy && (
          <>
            {strategyStateIsValid ? (
              <Box display='flex' alignItems='center'>
                <FilterAutocomplete
                  id='strategy-autocomplete'
                  dataTest='strategy-autocomplete'
                  label={currentStrategy?.name || ''}
                  placeholder='Strategy'
                  options={strategyOptions}
                  value={currentOptionStrategy}
                  setValue={onStrategySelect}
                  getOptionLabel={getDropdownLabel}
                  getOptionSelected={compareDropdownIds}
                  quickSuggest
                  autoSelect={false}
                />
              </Box>
            ) : (
              <Box>
                <InlineMessage
                  message={resolveErrorsMessage}
                  type={INLINE_MESSAGE_VARIANTS.ALERT}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      <Box>
        {loading ? (
          <Spinner position='relative' />
        ) : (
          <Grid container>
            <Grid item xs={8}>
              <Box px={3} py={2}>
                <Box mb={1}>
                  <Typography variant='h6' color='textPrimary' className={classes.subHeading}>
                    Edit Matrix
                  </Typography>
                </Box>
                <div>
                  {presentStrategies.map(strategy => (
                    <Box display='inline-block' mr={1} mb={2} key={strategy}>
                      <RadioOption
                        key={ValidStrategyMatrices[strategy]}
                        id={ValidStrategyMatrices[strategy]}
                        name={ValidStrategyMatrices[strategy]}
                        selected={currentMatrix === ValidStrategyMatrices[strategy]}
                        disabled={!strategyStateIsValid}
                        onChange={() => onSelectedMatrixChange(ValidStrategyMatrices[strategy])}>
                        <span className='mt1'>
                          {getStrategyNameByKey(ValidStrategyMatrices[strategy])}
                        </span>
                      </RadioOption>
                    </Box>
                  ))}
                </div>
                {currentMatrix === ValidStrategyMatrices.volumeToLengthOfHaul && (
                  <Box>
                    <InputMatrix
                      rows={volumeByLengthOfHaulRows}
                      columns={volumeByLengthOfHaulColumns}
                      matrix={volumeByLengthOfHaulMatrix}
                      endAdornment='%'
                      description={volToLoh?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.desirabilityToPickupMarket && (
                  <Box>
                    <InputMatrix
                      rows={desToPuMarketRows}
                      columns={desToPuMarketColumns}
                      matrix={desirabilityToPickupMarketMatrix}
                      endAdornment='%'
                      description={desToPuMarket?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.equipmentToPickupMarket && (
                  <Box>
                    <InputMatrix
                      rows={equipmentToPickupMarketRows}
                      columns={equipmentToPickupMarketColumns}
                      matrix={equipmentToPickupMarketMatrix}
                      endAdornment='%'
                      description={equipToPuMarket?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.volumeToDesirabilityLow && (
                  <Box>
                    <InputMatrix
                      rows={volumeToDesirabilityLowRows}
                      columns={volumeToDesirabilityLowColumns}
                      matrix={volumeToDesirabilityLowMatrix}
                      endAdornment='%'
                      description={volToDesirLow?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.volumeToDesirabilityMedium && (
                  <Box>
                    <InputMatrix
                      rows={volumeToDesirabilityMediumRows}
                      columns={volumeToDesirabilityMediumColumns}
                      matrix={volumeToDesirabilityMediumMatrix}
                      endAdornment='%'
                      description={volToDesirMed?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.volumeToDesirabilityHigh && (
                  <Box>
                    <InputMatrix
                      rows={volumeToDesirabilityHighRows}
                      columns={volumeToDesirabilityHighColumns}
                      matrix={volumeToDesirabilityHighMatrix}
                      endAdornment='%'
                      description={volToDesirHigh?.description}
                    />
                  </Box>
                )}
                {currentMatrix === ValidStrategyMatrices.lengthOfHaulToCostPctDat7 && (
                  <Box>
                    <InputMatrix
                      rows={lengthOfHaulToCostPctDat7Rows}
                      columns={lengthOfHaulToCostPctDat7Columns}
                      matrix={lengthOfHaulToCostPctDat7Matrix}
                      endAdornment='%'
                      description={lohToCostPctDat7?.description}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={4} className={classes.sidePanel}>
              <Box p={2}>
                {laneCriteria && laneCriteria.length > 1 && currentLaneCriteria && (
                  <Box mb={1}>
                    <Select
                      label='Drop Strategy'
                      value={getDropStrategy(currentLaneCriteria)}
                      options={generateDropStrategyOptions(laneCriteria)}
                      onChange={onDropStrategyChange}
                      placeholder='Drop Strategy'
                      required
                      large
                    />
                  </Box>
                )}
                {currentLaneCriteria && currentEditCategory === null && (
                  <>
                    <Box mb={2}>
                      <Box mb={1}>
                        <Typography variant='h6' color='textPrimary' className={classes.subHeading}>
                          Eligibility Criteria
                        </Typography>
                      </Box>
                      <Box mb={1}>Attributes that qualify for this strategy.</Box>
                      <Divider />
                      <EditableField
                        fieldName={EligibilityCriteriaFields.dropType}
                        fieldValue={getDropTypeDisplayValue(currentLaneCriteria)}
                        readOnly
                        onEditClick={() => {
                          editField(
                            EditCategories.eligibilityCriteria,
                            EligibilityCriteriaFields.dropType
                          )
                        }}
                      />
                      <Divider />
                      {currentLaneCriteria?.equipment && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.equipment}
                            fieldValue={getEquipmentDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.equipment
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.liveAuditRiskLevel && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.riskyAuditLevel}
                            fieldValue={getRiskyAuditDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.riskyAuditLevel
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.contractDurationInMonths && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.contractDuration}
                            fieldValue={getContractDurationDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.contractDuration
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.puDistMarketCenter && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.pickupMarketCenterDistance}
                            fieldValue={getPuMarketCenterDistanceDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.pickupMarketCenterDistance
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.delDistMarketCenter && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.deliveryMarketCenterDistance}
                            fieldValue={getDelMarketCenterDistanceDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.deliveryMarketCenterDistance
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.annualVolume && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.annualVolume}
                            fieldValue={getAnnualVolumeDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.annualVolume
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.marketDemandScore && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.marketDemandScore}
                            fieldValue={getMarketDemandScoreDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.marketDemandScore
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.extraStops && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.extraStops}
                            fieldValue={getExtraStopsDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.extraStops
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                      {currentLaneCriteria?.effectiveMileage && (
                        <>
                          <EditableField
                            fieldName={EligibilityCriteriaFields.effectiveMileage}
                            fieldValue={getEffectiveMileageDisplayValue(currentLaneCriteria)}
                            onEditClick={() => {
                              editField(
                                EditCategories.eligibilityCriteria,
                                EligibilityCriteriaFields.effectiveMileage
                              )
                            }}
                          />
                          <Divider />
                        </>
                      )}
                    </Box>
                    <Box>
                      <Box mb={1}>
                        <Typography variant='h6' color='textPrimary' className={classes.subHeading}>
                          Threshold Management
                        </Typography>
                      </Box>
                      <Box mb={1}>
                        Lanes will be marked with a &quot;variance flag&quot; when threshold is
                        exceeded.
                      </Box>
                      <Divider />
                      {thresholdManagementFieldsPresent(currentStrategy) ? (
                        <>
                          {getDatVariance(currentStrategy) && (
                            <>
                              <EditableField
                                fieldName={ThresholdManagementFields.datVariance}
                                fieldValue={getDatVarianceDisplayValue(currentStrategy)}
                                onEditClick={() => {
                                  editField(
                                    EditCategories.thresholdManagement,
                                    ThresholdManagementFields.datVariance
                                  )
                                }}
                              />
                              <Divider />
                            </>
                          )}
                          {getNegativeMarginRisk(currentStrategy) && (
                            <>
                              <EditableField
                                fieldName={ThresholdManagementFields.negativeMarginRisk}
                                fieldValue={getNegativeMarginRiskDisplayValue(currentStrategy)}
                                onEditClick={() => {
                                  editField(
                                    EditCategories.thresholdManagement,
                                    ThresholdManagementFields.negativeMarginRisk
                                  )
                                }}
                              />
                              <Divider />
                            </>
                          )}
                          {canDisplayMinimumMargin(currentStrategy) && (
                            <>
                              <EditableField
                                fieldName={ThresholdManagementFields.minimumMargin}
                                fieldValue={getMinimumMarginDisplayValue(currentStrategy)}
                                onEditClick={() => {
                                  editField(
                                    EditCategories.thresholdManagement,
                                    ThresholdManagementFields.minimumMargin
                                  )
                                }}
                              />
                              <Divider />
                            </>
                          )}
                        </>
                      ) : (
                        <Box mt={1}>N/A</Box>
                      )}
                    </Box>
                  </>
                )}
                {currentEditCategory !== null && (
                  <Box mb={2}>
                    <EditableFieldInput
                      currentField={currentEditField}
                      currentCategory={currentEditCategory}
                      currentLaneCriteria={currentLaneCriteria}
                      currentStrategy={currentStrategy}
                      onLaneCriteriaChange={onLaneCriteriaChange}
                      onStrategyChange={onStrategyChange}
                      onCancel={() => {
                        setCurrentEditCategory(null)
                        setCurrentEditField(null)
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  )
}
